import { coerceCsvCellValue } from "@/utils/structured-data-coerce";

/**
 * @typedef {import('@/types/structured-data-editor').SdeRow} SdeRow
 */

const DEFAULT_ASYNC_BATCH_SIZE = 200;

/**
 * @param {object[]} fieldDefs
 * @returns {Map<string, object>}
 */
export function buildCsvFieldDefMap(fieldDefs) {
    return new Map(fieldDefs.map((field) => [field.value, field]));
}

/**
 * Convert CSV matrix + mapping to editor rows (confidence = csv / 100%).
 *
 * @param {unknown[][]} data
 * @param {(string|null)[]} fieldsMapping
 * @param {object[]} fieldDefs
 * @param {{ startRowIndex?: number, fieldDefByValue?: Map<string, object> }} [options]
 * @returns {SdeRow[]}
 */
export function csvMatrixToSdeRows(data, fieldsMapping, fieldDefs, options = {}) {
    const fieldDefByValue = options.fieldDefByValue || buildCsvFieldDefMap(fieldDefs);
    const startRowIndex = options.startRowIndex || 0;
    return data.map((row, rowIndex) => ({
        rowIndex: startRowIndex + rowIndex,
        fields: csvRowToFields(row, fieldsMapping, fieldDefByValue),
        excluded: false,
        syncStatus: "pending",
    }));
}

/**
 * Build editor rows without blocking the main thread on large CSV files.
 *
 * @param {unknown[][]} data
 * @param {(string|null)[]} fieldsMapping
 * @param {object[]} fieldDefs
 * @param {{ batchSize?: number }} [options]
 * @returns {Promise<SdeRow[]>}
 */
export async function csvMatrixToSdeRowsAsync(
    data,
    fieldsMapping,
    fieldDefs,
    options = {}
) {
    const batchSize = options.batchSize || DEFAULT_ASYNC_BATCH_SIZE;
    const fieldDefByValue = buildCsvFieldDefMap(fieldDefs);
    const rows = [];

    for (let offset = 0; offset < data.length; offset += batchSize) {
        const chunk = data.slice(offset, offset + batchSize);
        rows.push(
            ...csvMatrixToSdeRows(chunk, fieldsMapping, fieldDefs, {
                startRowIndex: offset,
                fieldDefByValue,
            })
        );
        if (offset + batchSize < data.length) {
            await new Promise((resolve) => {
                setTimeout(resolve, 0);
            });
        }
    }

    return rows;
}

/**
 * @param {unknown[]} row
 * @param {(string|null)[]} fieldsMapping
 * @param {Map<string, object>} fieldDefByValue
 */
function csvRowToFields(row, fieldsMapping, fieldDefByValue) {
    /** @type {SdeRow['fields']} */
    const fields = {};
    row.forEach((cell, index) => {
        const mappedKey = fieldsMapping[index];
        if (!mappedKey) {
            return;
        }
        const fieldDef = fieldDefByValue.get(mappedKey);
        const parts = mappedKey.split(".");
        const lastField = parts[parts.length - 1];
        fields[mappedKey] = {
            key: mappedKey,
            value: coerceCsvCellValue(cell, fieldDef || {}, lastField),
            confidence: {
                score: 100,
                level: "csv",
            },
        };
    });
    return fields;
}
